'use client';

import ChevronDown from '@/assets/svgs/chevron-down.svg';
import MinusCircleSvg from '@/assets/svgs/jobs/minus-circle.svg';
import UserCircleIconSvg from '@/assets/svgs/main/canvassers-filled.svg';
import PlusIconSvg from '@/assets/svgs/plus.svg';
import { inter600 } from '@/config/fonts';
import { useFetchAgents } from '@/containers/Agents/api';
import AssignedAgentsValidationMessage from '@/containers/Agents/AssignedAgentsValidationMessage/AssignedAgentsValidationMessage';
import { Agent } from '@/containers/Agents/types';
import {
  selectAgentToAutoAssign,
  selectAssignedAgents,
  selectIsNewAgentCreated,
} from '@/containers/Jobs/redux/jobEditors.selectors';
import {
  setAssignAgent,
  setAssignAgentAutomatically,
  setIsNewAgentCreated,
  setUnassignAgent,
  updateAssignedAgent,
} from '@/containers/Jobs/redux/jobEditors.slice';
import { Modals } from '@/containers/Modals/all-modals';
import { useAppDispatch } from '@/lib/redux/store';
import { AssignmentList } from '@/shared/components/AssignmentList';
import IconButton from '@/shared/components/Buttons/IconButton/IconButton';
import { SearchBar } from '@/shared/components/SearchBar/SearchBar';
import { loseFocusFromActiveElement } from '@/shared/utils/dom';
import classNames from 'classnames';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import ModalBody from '../ModalBody';
import ModalContainer from '../ModalContainer';
import ModalHeader from '../ModalHeader';
import { addOpenModal } from '../redux/modals.slice';
import { AssignAgentsModalParams, ModalProps } from '../types';
import useModals from '../useModals';
import AgentItemView from './AgentItemView';
interface AssignAgentsModalProps extends ModalProps {
  params?: AssignAgentsModalParams;
}

const modalName = Modals.AssignAgentsModal;
const AssignAgentsModal = ({
  className,
  params,
  variant = 'default',
}: AssignAgentsModalProps) => {
  const modalBodyRef = useRef<HTMLDivElement>(null);
  const [isSearchEmpty, setIsSearchEmpty] = useState(true);
  const { onClose, isModalOpen } = useModals({
    modalName,
  });

  // Modal Specific details
  const dispatch = useAppDispatch();
  const jobId = params?.jobId || '';
  const agentToAutoAssign = useSelector(selectAgentToAutoAssign(jobId));
  const isNewAgentCreated = useSelector(selectIsNewAgentCreated(jobId));

  const identityId = params?.identityId;
  const {
    isLoading: isLoadingAgents,
    data: agents,
    refetch: refetchAgents,
  } = useFetchAgents(identityId, !!identityId);
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState<Agent[]>([]);
  const [shouldRefreshList, setShouldRefreshList] = useState(false);

  // Auto Assign Agent when created
  useEffect(() => {
    if (agentToAutoAssign) {
      setShouldRefreshList(true);
      dispatch(setAssignAgent({ jobId, assignment: agentToAutoAssign }));
      dispatch(setAssignAgentAutomatically({ jobId, agent: undefined }));
      dispatch(setIsNewAgentCreated({ jobId, isCreated: false }));
    } else if (isNewAgentCreated) {
      setShouldRefreshList(true);
      dispatch(setIsNewAgentCreated({ jobId, isCreated: false }));
    }
  }, [agentToAutoAssign, isNewAgentCreated, dispatch, jobId]);

  // Refresh list
  useEffect(() => {
    if (shouldRefreshList) {
      refetchAgents();
      setShouldRefreshList(false);
    }
  }, [refetchAgents, shouldRefreshList]);

  // Search
  useEffect(() => {
    setIsSearchEmpty(searchText === '');
    if (!searchText) {
      return setSearchResults(agents?.data ?? []);
    }

    setSearchResults(
      agents?.data.filter(
        (item) =>
          item.display_email.toLowerCase().includes(searchText.toLowerCase()) ||
          item.display_name.toLowerCase().includes(searchText.toLowerCase())
      ) ?? []
    );
  }, [searchText, agents?.data]);

  const assignedAgentItems = useSelector(selectAssignedAgents(jobId));
  const assignedAgents = useMemo(() => {
    return assignedAgentItems
      .map((item) => agents?.data.find((cl) => cl.id === item.agent_id) as Agent)
      .filter(Boolean);
  }, [agents?.data, assignedAgentItems]);

  const availableAgents = useMemo(() => {
    const assignedAgentIds = assignedAgentItems.map((list) => list.agent_id);
    return searchResults
      .filter((item) => !assignedAgentIds.includes(item.id))
      .filter(Boolean);
  }, [assignedAgentItems, searchResults]);

  const handleAssign = (agent: Agent) => {
    dispatch(
      setAssignAgent({
        jobId,
        assignment: {
          agent_id: agent.id,
        },
      })
    );
  };

  const handleUnassign = (agent: Agent) => {
    dispatch(setUnassignAgent({ jobId, agentId: agent.id }));
  };

  const handleAssignAll = () => {
    availableAgents.forEach((agent) => {
      dispatch(
        setAssignAgent({
          jobId,
          assignment: {
            agent_id: agent.id,
          },
        })
      );
    });
  };

  const unmount = () => {};

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => unmount, []);

  return (
    <div className={classNames('relative', className)}>
      <input
        type="checkbox"
        id={modalName}
        className="modal-toggle"
        checked={isModalOpen}
        onChange={() => {}}
      />
      <ModalContainer
        isModalOpen={isModalOpen}
        modalName={modalName}
        isSearchEmpty={isSearchEmpty}
        variant={variant}
        onClose={onClose}
        shouldCloseOnEscape>
        <div className="flex h-full flex-col overflow-hidden">
          {/* Stick to top */}
          <ModalHeader
            modalName={modalName}
            closeAction="state"
            icon={<UserCircleIconSvg className="h-5 w-5" />}
            title="Canvassers"
          />

          {/* Take up remaining space and enable inside-scrolling */}
          <ModalBody ref={modalBodyRef} className="px-5 !py-0">
            {/* Action button */}
            <div className="flex h-[84px] items-center justify-end">
              <button
                className="btn btn-primary btn-sm flex gap-1 pl-1.5"
                onClick={() => {
                  dispatch(
                    addOpenModal({
                      modalName: Modals.CreateNewAgent,
                      modalParams: JSON.stringify({
                        jobId,
                      }),
                    })
                  );
                }}>
                <PlusIconSvg className="h-5 w-5" />
                Create New
              </button>
            </div>

            {/* Search */}
            <div className="search-container mb-6">
              <SearchBar
                search={searchText}
                setSearch={setSearchText}
                placeholder="Search canvassers..."
                debounceMs={500}
                ariaLabel="Search canvassers"
                className="w-full"
              />
            </div>

            <AssignedAgentsValidationMessage className="mb-6 !px-0" jobId={jobId} />

            {/* Assignment List with custom assigned actions */}
            <AssignmentList
              assignedItems={assignedAgents}
              availableItems={availableAgents}
              LineItemView={(props) => (
                <AgentItemView
                  {...props}
                  actions={
                    // For assigned items, show dropdown + remove button
                    assignedAgents.some((a) => a.id === props.data.id) ? (
                      <div className="actions flex items-center gap-2">
                        <div className="dropdown" data-dropdown-dismiss="true">
                          <div
                            tabIndex={0}
                            role="button"
                            className={classNames(
                              'btn btn-outline m-1 flex !h-8 w-[155px] justify-between border-blue-200 !px-2 shadow-sm',
                              inter600.className
                            )}>
                            {assignedAgentItems.find((a) => a.agent_id === props.data.id)
                              ?.isResponder
                              ? 'Responder'
                              : 'Non-Responder'}
                            <ChevronDown className="text-neutral-default" />
                          </div>
                          <ul
                            tabIndex={0}
                            className="menu dropdown-content rounded-box bg-base-100 w-52 p-2 shadow">
                            <li>
                              <span
                                onClick={() => {
                                  loseFocusFromActiveElement();
                                  dispatch(
                                    updateAssignedAgent({
                                      jobId,
                                      id: props.data.id,
                                      props: {
                                        isResponder: true,
                                      },
                                    })
                                  );
                                }}>
                                Responder
                              </span>
                            </li>
                            <li>
                              <span
                                onClick={() => {
                                  loseFocusFromActiveElement();
                                  dispatch(
                                    updateAssignedAgent({
                                      jobId,
                                      id: props.data.id,
                                      props: {
                                        isResponder: false,
                                      },
                                    })
                                  );
                                }}>
                                Non-Responder
                              </span>
                            </li>
                          </ul>
                        </div>
                        <IconButton
                          className="btn-outline border-blue-200 shadow-sm"
                          title="Remove"
                          icon={<MinusCircleSvg />}
                          onClick={() => handleUnassign(props.data)}
                        />
                      </div>
                    ) : (
                      // For available items, just show default actions passed by AssignmentList
                      props.actions
                    )
                  }
                />
              )}
              onAssign={handleAssign}
              onUnassign={handleUnassign}
              onAssignAll={handleAssignAll}
              isLoading={isLoadingAgents}
              searchText={searchText}
              emptyMessage={
                <div className="flex flex-col gap-3">
                  <span>No Agents found</span>
                  {searchText && (
                    <button
                      className="btn btn-outline btn-sm border-gray-200 shadow-sm"
                      onClick={() => setSearchText('')}>
                      Reset search
                    </button>
                  )}
                  <button
                    className="btn btn-outline btn-sm border-gray-200 shadow-sm"
                    onClick={() =>
                      dispatch(
                        addOpenModal({
                          modalName: Modals.CreateNewAgent,
                          modalParams: JSON.stringify({
                            jobId,
                          }),
                        })
                      )
                    }>
                    Create New
                  </button>
                </div>
              }
              getItemKey={(agent) => agent.id}
              assignButtonLabel="Assign"
              unassignButtonLabel="Remove"
            />
          </ModalBody>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={onClose}>close</button>
        </form>
      </ModalContainer>
    </div>
  );
};

export default AssignAgentsModal;
